import { useState, useEffect } from "react";
import axios from "axios";
import { PlusCircle, Send, X, AlertCircle, CheckCircle } from "lucide-react";
import { toast } from "../hooks/use-toast";
import { useLanguage } from "../contexts/LanguageContext";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Generate a unique client token
const getClientToken = () => {
  let token = localStorage.getItem("client_request_token");
  if (!token) {
    token = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("client_request_token", token);
  }
  return token;
};

const RequestCodeForm = () => {
  const { t } = useLanguage();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    app_name: "",
    google_play_link: "",
    other_details: "",
  });
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captchaQuestion, setCaptchaQuestion] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [requestsInfo, setRequestsInfo] = useState({
    requests_today: 0,
    remaining_requests: 5,
    daily_limit: 5,
  });

  useEffect(() => {
    if (showForm) {
      generateCaptcha();
      checkRequestLimit();
    }
  }, [showForm]);

  const generateCaptcha = async () => {
    setCaptchaAnswer("");
    try {
      const response = await axios.get(`${API}/captcha/challenge`);
      setCaptchaQuestion(response.data);
    } catch (error) {
      setCaptchaQuestion(null);
    }
  };

  const checkRequestLimit = async () => {
    try {
      const clientToken = getClientToken();
      const response = await axios.get(
        `${API}/code-requests/check-limit?client_token=${clientToken}`
      );
      setRequestsInfo(response.data);
    } catch (error) {
      console.error("Error checking limit:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.app_name.trim()) {
      toast({
        title: "Error",
        description: t('applicationNameRequired'),
        variant: "destructive",
      });
      return;
    }

    // Captcha is validated server-side (one-time use challenge)
    if (!captchaQuestion || !captchaAnswer) {
      toast({
        title: "Error",
        description: t('incorrectCaptcha'),
        variant: "destructive",
      });
      return;
    }

    try {
      setSubmitting(true);
      const clientToken = getClientToken();

      const response = await axios.post(`${API}/code-requests`, {
        ...formData,
        client_token: clientToken,
        captcha_id: captchaQuestion.captcha_id,
        captcha_answer: parseInt(captchaAnswer),
      });

      toast({
        title: "Success!",
        description: t('requestsRemaining').replace('{count}', response.data.remaining_requests).replace('{s}', response.data.remaining_requests !== 1 ? 's' : ''),
      });

      // Reset form
      setFormData({
        app_name: "",
        google_play_link: "",
        other_details: "",
      });
      setCaptchaAnswer("");
      setShowForm(false);
      checkRequestLimit();
    } catch (error) {
      console.error("Error submitting request:", error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || t('requestFailed'),
        variant: "destructive",
      });
      
      // Challenge is one-time use - fetch a new one after any failed attempt
      generateCaptcha();
      
      if (error.response?.status === 429) {
        // Rate limit reached
        checkRequestLimit();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowForm(false);
    setFormData({
      app_name: "",
      google_play_link: "",
      other_details: "",
    });
    setCaptchaAnswer("");
  };

  return (
    <div className="request-code-widget">
      {!showForm ? (
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold px-6 py-3 rounded-lg shadow-lg transition-all transform hover:scale-105"
        >
          <PlusCircle className="w-5 h-5" />
          {t('requestNewCode')}
        </button>
      ) : (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">{t('requestCodeTitle')}</h3>
                  <p className="text-blue-100 text-sm mt-1">
                    {t('requestCodeSubtitle')}
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Request Limit Info */}
            <div className="px-6 pt-4">
              <div className={`flex items-center gap-2 p-3 rounded-lg ${
                requestsInfo.remaining_requests > 0 
                  ? "bg-blue-50 text-blue-700" 
                  : "bg-red-50 text-red-700"
              }`}>
                {requestsInfo.remaining_requests > 0 ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <span className="text-sm font-medium">
                  {requestsInfo.remaining_requests > 0 
                    ? t('requestsRemaining').replace('{count}', requestsInfo.remaining_requests).replace('{s}', requestsInfo.remaining_requests !== 1 ? 's' : '')
                    : t('dailyLimitReached')
                  }
                </span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Application Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('applicationName')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.app_name}
                  onChange={(e) =>
                    setFormData({ ...formData, app_name: e.target.value })
                  }
                  placeholder={t('applicationNamePlaceholder')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  maxLength="100"
                  required
                />
              </div>

              {/* Google Play Link */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('googlePlayLinkOptional')}
                </label>
                <input
                  type="url"
                  value={formData.google_play_link}
                  onChange={(e) =>
                    setFormData({ ...formData, google_play_link: e.target.value })
                  }
                  placeholder={t('googlePlayLinkPlaceholder')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Other Details */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('additionalDetailsOptional')}
                </label>
                <textarea
                  value={formData.other_details}
                  onChange={(e) =>
                    setFormData({ ...formData, other_details: e.target.value })
                  }
                  placeholder={t('additionalDetailsPlaceholder')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows="4"
                  maxLength="500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.other_details.length}/500 characters
                </p>
              </div>

              {/* Captcha */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('verifyHumanRequired')} <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <div className="bg-gray-100 px-4 py-3 rounded-lg border border-gray-300">
                    <span className="font-mono text-lg font-semibold" data-testid="request-code-captcha-question">
                      {captchaQuestion ? captchaQuestion.num1 : '…'} + {captchaQuestion ? captchaQuestion.num2 : '…'} = ?
                    </span>
                  </div>
                  <input
                    type="number"
                    value={captchaAnswer}
                    onChange={(e) => setCaptchaAnswer(e.target.value)}
                    placeholder={t('captchaAnswer')}
                    className="w-24 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="button"
                    onClick={generateCaptcha}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    {t('newQuestion')}
                  </button>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={submitting || requestsInfo.remaining_requests === 0}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      {t('submitting')}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      {t('submitRequest')}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestCodeForm;
